import { inject, injectable } from 'inversify'
import { DialogRepository } from '@core/infra/dialog/dialog.repository'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { DraftRepository, type DraftKey } from './draft.repository'

/**
 * The other half of unsaved-work protection: the half a Router never sees.
 *
 * `LeaveConfirmationService` guards *navigation*, but the way a draft is
 * actually lost in a list-and-pane app is smaller than that — the user opens a
 * second row's form, or closes the pane, while the first one still holds typed
 * text. No route changes, so nothing asks.
 *
 * One service rather than the check written into each open action, so "what
 * happens to a draft you are about to abandon" is answered identically by every
 * form and can be changed for all of them at once.
 */
@injectable()
export class DraftGuardService {
  @inject(DraftRepository)
  private draftRepository!: DraftRepository

  @inject(DialogRepository)
  private dialogRepository!: DialogRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  /**
   * True when the caller may go ahead — either nothing was at stake, or the user
   * said to throw it away, in which case it is already gone. False means the
   * user chose to keep editing and the caller must do nothing at all.
   */
  allowDiscard = async (key: DraftKey): Promise<boolean> => {
    if (!this.draftRepository.isDirty(key)) return true

    const confirmed = await this.dialogRepository.confirm(this.i18nRepository.t('drafts.confirmDiscard'), {
      confirmLabel: this.i18nRepository.t('drafts.discard'),
      cancelLabel: this.i18nRepository.t('drafts.keepEditing'),
    })
    if (!confirmed) return false

    // Cleared here rather than by the caller: a caller that forgot would leave a
    // dirty draft behind and prompt about it again on the next unrelated action.
    this.draftRepository.clear(key)
    return true
  }
}
